import {
  sanitizePsoriasisEntryDataResponse,
  processPsoriasisResults,
} from "../../../../../../../helpers/apiUtils/psoriasisUtils";
import {
  processSettingImagePairs,
  analyzeCeleryStatus,
} from "../../../../../../../helpers/apiUtils/commonUtils";

import cloneDeep from "lodash.clonedeep";

// Main function to process the response after the entry is sent
export const entrySentResponse = ({
  response,
  getTagSuggestions,
  setEntryData,
  setCeleryID,
  setCalculationProgress,
  setComparisonItems,
  setInitialComparisonItems,
  setProcessedComparisonItems,
  setAppointmentDate,
  setInitialAppointmentDate,
  setTags,
  setInitialTags,
  setSummary,
  setInitialSummary,
  setNotes,
  setInitialNotes,
  setSemanticZC,
  setInitialSemanticZC,
  setProcessedSemanticZC,
  setClassificationZC,
  setInitialClassificationZC,
  setProcessedClassificationZC,
  setPsoriasisCalculationData,
  getCeleryStatus,
  axiosFetch,
}) => {
  if (response?.action_code === "entry_sent") {
    getTagSuggestions();

    const entryDataResponse = response?.payload?.entry_data;
    if (entryDataResponse) {
      const sanitizedEntryDataResponse = sanitizeAndSetEntryData({
        entryDataResponse,
        setEntryData,
      });

      const celeryTaskStatusCode = analyzeCeleryStatus({
        sanitizedEntryDataResponse,
        setCeleryID,
      });

      if (celeryTaskStatusCode === "completed") {
        // console.log("Calculation completed");
        handleCompletedCeleryTask({
          sanitizedEntryDataResponse,
          setCalculationProgress,
          setComparisonItems,
          setInitialComparisonItems,
          setProcessedComparisonItems,
          setAppointmentDate,
          setInitialAppointmentDate,
          setTags,
          setInitialTags,
          setSummary,
          setInitialSummary,
          setNotes,
          setInitialNotes,
          setSemanticZC,
          setInitialSemanticZC,
          setProcessedSemanticZC,
          setClassificationZC,
          setInitialClassificationZC,
          setProcessedClassificationZC,
          setPsoriasisCalculationData,
        });
      } else if (celeryTaskStatusCode === "in_progress") {
        // console.log("Calculation in progress");
        handleIncompleteCeleryTask({
          sanitizedEntryDataResponse,
          currentCeleryTaskId: sanitizedEntryDataResponse.celery_task_id,
          getCeleryStatus,
          axiosFetch,
          setComparisonItems,
          setInitialComparisonItems,
          setProcessedComparisonItems,
          setAppointmentDate,
          setInitialAppointmentDate,
          setTags,
          setInitialTags,
          setSummary,
          setInitialSummary,
          setNotes,
          setInitialNotes,
          setSemanticZC,
          setInitialSemanticZC,
          setProcessedSemanticZC,
          setClassificationZC,
          setInitialClassificationZC,
          setProcessedClassificationZC,
          setPsoriasisCalculationData,
        });
      } else if (celeryTaskStatusCode === "new_entry") {
        // console.log("New Entry");

        // Set appointment date
        setAppointmentDate(sanitizedEntryDataResponse.appointment_date);
        setInitialAppointmentDate(sanitizedEntryDataResponse.appointment_date);

        setDefaultZoomCoefficients(
          sanitizedEntryDataResponse,
          setSemanticZC,
          setInitialSemanticZC,
          setProcessedSemanticZC,
          setClassificationZC,
          setInitialClassificationZC,
          setProcessedClassificationZC
        );
      }
    }
  }
};

// Function to sanitize and set the entry data
const sanitizeAndSetEntryData = ({ entryDataResponse, setEntryData }) => {
  const sanitizedEntryDataResponse = sanitizePsoriasisEntryDataResponse({
    entryDataResponse,
  });
  setEntryData(sanitizedEntryDataResponse);
  return sanitizedEntryDataResponse;
};

// Function to handle the completed Celery task
const handleCompletedCeleryTask = ({
  sanitizedEntryDataResponse,
  setCalculationProgress,
  setComparisonItems,
  setInitialComparisonItems,
  setProcessedComparisonItems,
  setAppointmentDate,
  setInitialAppointmentDate,
  setTags,
  setInitialTags,
  setSummary,
  setInitialSummary,
  setNotes,
  setInitialNotes,
  setSemanticZC,
  setInitialSemanticZC,
  setProcessedSemanticZC,
  setClassificationZC,
  setInitialClassificationZC,
  setProcessedClassificationZC,
  setPsoriasisCalculationData,
}) => {
  // console.log("Celery task complete");
  updateCalculationProgressToSuccess({ setCalculationProgress });

  setProcessedImages({
    sanitizedEntryDataResponse,
    celeryTaskCompleted: true,
    setComparisonItems,
    setInitialComparisonItems,
    setProcessedComparisonItems,
  });
  setAdditionalStateItems({
    sanitizedEntryDataResponse,
    setAppointmentDate,
    setInitialAppointmentDate,
    setTags,
    setInitialTags,
    setSummary,
    setInitialSummary,
    setNotes,
    setInitialNotes,
    setSemanticZC,
    setInitialSemanticZC,
    setProcessedSemanticZC,
    setClassificationZC,
    setInitialClassificationZC,
    setProcessedClassificationZC,
    setPsoriasisCalculationData,
  });

  const processedPsoriasisData = processPsoriasisResults({
    sanitizedEntryDataResponse,
  });
  setPsoriasisCalculationData(processedPsoriasisData);
};

// Function to handle the incomplete Celery task
const handleIncompleteCeleryTask = ({
  sanitizedEntryDataResponse,
  currentCeleryTaskId,
  getCeleryStatus,
  axiosFetch,
  setComparisonItems,
  setInitialComparisonItems,
  setProcessedComparisonItems,
  setAppointmentDate,
  setInitialAppointmentDate,
  setTags,
  setInitialTags,
  setSummary,
  setInitialSummary,
  setNotes,
  setInitialNotes,
  setSemanticZC,
  setInitialSemanticZC,
  setProcessedSemanticZC,
  setClassificationZC,
  setInitialClassificationZC,
  setProcessedClassificationZC,
  setPsoriasisCalculationData,
}) => {
  // console.log("Celery task in progress");
  setProcessedImages({
    sanitizedEntryDataResponse,
    celeryTaskCompleted: false,
    setComparisonItems,
    setInitialComparisonItems,
    setProcessedComparisonItems,
  });
  setAdditionalStateItems({
    sanitizedEntryDataResponse,
    setAppointmentDate,
    setInitialAppointmentDate,
    setTags,
    setInitialTags,
    setSummary,
    setInitialSummary,
    setNotes,
    setInitialNotes,
    setSemanticZC,
    setInitialSemanticZC,
    setProcessedSemanticZC,
    setClassificationZC,
    setInitialClassificationZC,
    setProcessedClassificationZC,
    setPsoriasisCalculationData,
  });

  if (currentCeleryTaskId) {
    getCeleryStatus({ axiosFetch, celeryTaskId: currentCeleryTaskId });
  }
};

// Function to update calculation progress to success
const updateCalculationProgressToSuccess = ({ setCalculationProgress }) => {
  setCalculationProgress((prevState) => ({
    ...prevState,
    calculationComplete: true,
    calculationSuccessful: true,
    calculationPending: false,
    state: "SUCCESS",
  }));
};

// Function to set processed images
const setProcessedImages = ({
  sanitizedEntryDataResponse,
  celeryTaskCompleted,
  setComparisonItems,
  setInitialComparisonItems,
  setProcessedComparisonItems,
}) => {
  const { processedComparisonItems } = processSettingImagePairs({
    sanitizedEntryDataResponse,
    celeryTaskCompleted,
    imagePairs: celeryTaskCompleted
      ? [
          {
            image_one_name: "original_image",
            image_two_name: "overlay",
          },
        ]
      : [
          {
            image_one_name: "original_image",
          },
        ],
  });

  if (processedComparisonItems.length !== 0) {
    setComparisonItems([...processedComparisonItems]);
    setInitialComparisonItems(cloneDeep(processedComparisonItems));
    setProcessedComparisonItems(cloneDeep(processedComparisonItems));
  }
};

// Function to set additional state items
const setAdditionalStateItems = ({
  sanitizedEntryDataResponse,
  setAppointmentDate,
  setInitialAppointmentDate,
  setTags,
  setInitialTags,
  setSummary,
  setInitialSummary,
  setNotes,
  setInitialNotes,
  setSemanticZC,
  setInitialSemanticZC,
  setProcessedSemanticZC,
  setClassificationZC,
  setInitialClassificationZC,
  setProcessedClassificationZC,
  setPsoriasisCalculationData,
}) => {
  setTags([...sanitizedEntryDataResponse.classification_tags]);
  setInitialTags([...sanitizedEntryDataResponse.classification_tags]);

  setAppointmentDate(sanitizedEntryDataResponse.appointment_date);
  setInitialAppointmentDate(sanitizedEntryDataResponse.appointment_date);

  setSummary(sanitizedEntryDataResponse.summary);
  setInitialSummary(sanitizedEntryDataResponse.summary);

  setNotes(sanitizedEntryDataResponse.notes);
  setInitialNotes(sanitizedEntryDataResponse.notes);

  setSemanticZC(sanitizedEntryDataResponse.semantic_zc);
  setInitialSemanticZC(sanitizedEntryDataResponse.semantic_zc);
  setProcessedSemanticZC(sanitizedEntryDataResponse.semantic_zc);

  setClassificationZC(sanitizedEntryDataResponse.classification_zc);
  setInitialClassificationZC(sanitizedEntryDataResponse.classification_zc);
  setProcessedClassificationZC(sanitizedEntryDataResponse.classification_zc);

  const processedPsoriasisData = processPsoriasisResults({
    sanitizedEntryDataResponse: sanitizedEntryDataResponse,
  });
  setPsoriasisCalculationData(processedPsoriasisData);
};

const setDefaultZoomCoefficients = (
  sanitizedEntryDataResponse,
  setSemanticZC,
  setInitialSemanticZC,
  setProcessedSemanticZC,
  setClassificationZC,
  setInitialClassificationZC,
  setProcessedClassificationZC
) => {
  setSemanticZC(sanitizedEntryDataResponse.semantic_zc);
  setInitialSemanticZC(sanitizedEntryDataResponse.semantic_zc);
  setProcessedSemanticZC(sanitizedEntryDataResponse.semantic_zc);

  setClassificationZC(sanitizedEntryDataResponse.classification_zc);
  setInitialClassificationZC(sanitizedEntryDataResponse.classification_zc);
  setProcessedClassificationZC(sanitizedEntryDataResponse.classification_zc);
};
